/**
 * useMarkdown.ts - Markdown to HTML Conversion Hook (NPM Version)
 *
 * Converts markdown content to HTML using the marked library (npm package).
 * Optimized for streaming content with memoization for performance.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { marked } from 'marked';

// Configure marked globally once
marked.setOptions({
    breaks: true,      // Convert \n to <br>
    gfm: true,         // GitHub Flavored Markdown
});

/**
 * Synchronously parse markdown to HTML
 */
export const parseMarkdown = (markdown: string): string => {
    if (!markdown) return '';
    
    try {
        // marked.parse returns string when async is false (default)
        return marked.parse(markdown) as string;
    } catch (error) {
        console.error('Error parsing markdown:', error);
        return markdown;
    }
};

interface UseMarkdownOptions {
    /** Debounce delay for streaming content (ms) */
    debounceMs?: number;
    /** Whether content is actively streaming */
    isStreaming?: boolean;
}

/**
 * Hook for converting markdown to HTML
 * Optimized for both static and streaming content
 */
export const useMarkdown = (
    markdown: string,
    options: UseMarkdownOptions = {}
): { html: string; isLoading: boolean; error: null } => {
    const { debounceMs = 50, isStreaming = false } = options;
    
    const [html, setHtml] = useState<string>(() => 
        isStreaming ? '' : parseMarkdown(markdown)
    );
    
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const lastLengthRef = useRef<number>(0);

    // For non-streaming content, parse immediately with memoization
    const staticHtml = useMemo(() => {
        if (isStreaming) return '';
        return parseMarkdown(markdown);
    }, [markdown, isStreaming]);

    // Handle streaming content with debouncing
    useEffect(() => {
        if (!isStreaming) {
            setHtml(staticHtml);
            return;
        }

        // Clear any pending debounce
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // For streaming: debounce unless there's a significant change
        const lengthDiff = markdown.length - lastLengthRef.current;
        
        if (lengthDiff > 100) {
            // Large chunk received, update immediately
            setHtml(parseMarkdown(markdown));
            lastLengthRef.current = markdown.length;
        } else {
            // Small chunk, debounce
            debounceTimerRef.current = setTimeout(() => {
                setHtml(parseMarkdown(markdown));
                lastLengthRef.current = markdown.length;
            }, debounceMs);
        }

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [markdown, isStreaming, debounceMs, staticHtml]);

    return { 
        html: isStreaming ? html : staticHtml, 
        isLoading: false, 
        error: null 
    };
};

/**
 * Simple synchronous hook when you don't need streaming optimization
 */
export const useMarkdownSync = (markdown: string): string => {
    return useMemo(() => parseMarkdown(markdown), [markdown]);
};

export default useMarkdown;